import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  checkActionCode
} from 'firebase/auth';
import { auth, db } from './firebase.js';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import {
  initializeSubscription,
  cleanupSubscription,
  handleCheckoutSuccess,
  handleCheckoutCanceled
} from './subscription.js';

/**
 * Authentication module for BudgetApp
 */

// Global auth state
let currentUser = null;
let isSigningIn = false;
let authInitialized = false;
let loadStartTime = Date.now();
let verificationResendTimestamp = 0;
const RESEND_COOLDOWN_MS = 60000;
let _onUserChangeCallback = null;

// DOM elements
const appLoadingContainer = document.getElementById('app-loading-container');
const authContainer = document.getElementById('auth-container');
const mainContainer = document.getElementById('main-container');
const authLoading = document.getElementById('auth-loading');
const authError = document.getElementById('auth-error');
const authSuccess = document.getElementById('auth-success');
const signinForm = document.getElementById('signin-form');
const signupForm = document.getElementById('signup-form');
const authToggleLink = document.getElementById('auth-toggle-link');
const authToggleText = document.getElementById('auth-toggle-text');

// Send password reset email
export async function sendPasswordReset(email) {
  const actionCodeSettings = { url: window.location.origin };
  try {
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
    return { success: true };
  } catch (error) {
    console.error('Password reset error:', error);
    return { success: false, error: getErrorMessage(error.code) };
  }
}

// Show password reset form in the login box
export function showPasswordResetModal() {
  // Get form elements fresh (in case they weren't available during initialization)
  const currentSigninForm = document.getElementById('signin-form');
  const currentSignupForm = document.getElementById('signup-form');
  
  // Hide the sign-in and sign-up forms
  if (currentSigninForm) currentSigninForm.classList.add('hidden');
  if (currentSignupForm) currentSignupForm.classList.add('hidden');
  
  // Show or create password reset form
  let resetForm = document.getElementById('password-reset-form-inline');
  
  if (!resetForm) {
    // Create the password reset form if it doesn't exist
    resetForm = createPasswordResetForm();
  }
  
  if (resetForm) {
    resetForm.classList.remove('hidden');
    
    // Focus on the email input
    setTimeout(() => {
      const emailInput = document.getElementById('reset-email-inline');
      if (emailInput) {
        emailInput.focus();
      }
    }, 100);
  }
}

// Create inline password reset form
function createPasswordResetForm() {
  // Try multiple possible container selectors
  let authContainer = document.querySelector('.auth-form-column');
  if (!authContainer) {
    authContainer = document.querySelector('.auth-container');
  }
  if (!authContainer) {
    authContainer = document.getElementById('auth-container');
  }
  
  if (!authContainer) {
    return null;
  }
  
  const resetForm = document.createElement('form');
  resetForm.id = 'password-reset-form-inline';
  resetForm.className = 'auth-form';
  
  resetForm.innerHTML = `
    <h2 style="text-align: center; margin-bottom: 24px; color: var(--text-dark);">
      🔑 Reset Password
    </h2>
    <p style="text-align: center; margin-bottom: 24px; color: var(--text-light); font-size: 0.9em;">
      Enter your email address and we'll send you a link to reset your password.
    </p>
    
    <div class="form-group">
      <label for="reset-email-inline">Email Address</label>
      <input type="email" id="reset-email-inline" name="email" required placeholder="Enter your email">
    </div>
    
    <div class="error" id="password-reset-error-inline" style="display: none;"></div>
    <div class="success" id="password-reset-success-inline" style="display: none;"></div>
    
    <button type="submit" class="btn primary full-width" style="margin-bottom: 16px;">
      Send Reset Link
    </button>
    
    <div style="text-align: center;">
      <a href="#" id="back-to-login-link" style="color: var(--primary-color); text-decoration: none; font-size: 0.9em;">
        ← Back to Sign In
      </a>
    </div>
  `;
  
  // Add event listeners
  resetForm.addEventListener('submit', handlePasswordResetInlineSubmit);
  
  const backLink = resetForm.querySelector('#back-to-login-link');
  if (backLink) {
    backLink.addEventListener('click', (e) => {
      e.preventDefault();
      hidePasswordResetForm();
    });
  }
  
  // Insert the form into the auth container
  const signupForm = document.getElementById('signup-form');
  if (signupForm && signupForm.parentNode) {
    signupForm.parentNode.insertBefore(resetForm, signupForm.nextSibling);
  } else {
    // Fallback: append directly to auth container
    authContainer.appendChild(resetForm);
  }
  
  return resetForm;
}

// Handle inline password reset form submission
async function handlePasswordResetInlineSubmit(e) {
  e.preventDefault();
  
  const emailInput = document.getElementById('reset-email-inline');
  const errorEl = document.getElementById('password-reset-error-inline');
  const successEl = document.getElementById('password-reset-success-inline');
  
  if (!emailInput || !errorEl || !successEl) return;
  
  const email = emailInput.value.trim();
  
  // Clear previous messages
  errorEl.style.display = 'none';
  successEl.style.display = 'none';
  
  if (!email) {
    errorEl.textContent = 'Please enter your email address.';
    errorEl.style.display = 'block';
    return;
  }
  
  
  try {
    const result = await sendPasswordReset(email);
    
    if (result.success) {
      successEl.textContent = 'Password reset link sent! Check your email for instructions.';
      successEl.style.display = 'block';
      emailInput.value = ''; // Clear the input
    } else {
      errorEl.textContent = result.error;
      errorEl.style.display = 'block';
    }
  } catch (error) {
    console.error('Password reset error:', error);
    errorEl.textContent = 'Failed to send password reset email. Please try again.';
    errorEl.style.display = 'block';
  }
}

// Hide password reset form and show login form
function hidePasswordResetForm() {
  const resetForm = document.getElementById('password-reset-form-inline');
  if (resetForm) {
    resetForm.classList.add('hidden');
  }
  
  // Get form elements fresh
  const currentSigninForm = document.getElementById('signin-form');
  const currentSignupForm = document.getElementById('signup-form');
  
  // Show sign-in form
  if (currentSigninForm) {
    currentSigninForm.classList.remove('hidden');
  }
  
  // Hide sign-up form
  if (currentSignupForm) {
    currentSignupForm.classList.add('hidden');
  }
  
  // Update toggle link
  const authToggleText = document.getElementById('auth-toggle-text');
  if (authToggleText) {
    authToggleText.innerHTML = 'Don\'t have an account? <a id="auth-toggle-link">Start for free</a>';
  }
}

// Close password reset modal
export function closePasswordResetModal() {
  const modal = document.getElementById('password-reset-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

// Handle password reset form submission
export async function handlePasswordResetSubmit() {
  const emailInput = document.getElementById('reset-email');
  const errorEl = document.getElementById('password-reset-error');
  const successEl = document.getElementById('password-reset-success');
  
  if (!emailInput || !errorEl || !successEl) return;
  
  const email = emailInput.value.trim();
  
  if (!email) {
    errorEl.textContent = 'Please enter your email address.';
    errorEl.style.display = 'block';
    successEl.style.display = 'none';
    return;
  }
  
  const result = await sendPasswordReset(email);
  
  if (result.success) {
    successEl.textContent = 'Password reset link sent! Check your email.';
    successEl.style.display = 'block';
    errorEl.style.display = 'none';
    // Close modal after 2 seconds
    setTimeout(() => {
      closePasswordResetModal();
    }, 2000);
  } else {
    errorEl.textContent = result.error;
    errorEl.style.display = 'block';
    successEl.style.display = 'none';
  }
}

// Sign in with email and password
export async function signIn(email, password) {
  if (isSigningIn) return;
  
  isSigningIn = true;
  try {
    await signInWithEmailAndPassword(auth, email, password);
    showSuccess('Successfully signed in!');
  } catch (error) {
    console.error('Sign-in error:', error);
    showError(getErrorMessage(error.code));
  } finally {
    isSigningIn = false;
  }
}

// Sign up with email and password
export async function signUp(email, password) {
  if (isSigningIn) return;

  isSigningIn = true;
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await createUserData(userCredential.user);
    await sendVerificationEmail(userCredential.user);
    // onAuthStateChanged fires next and routes to showVerificationPending()
    isSigningIn = false;
  } catch (error) {
    console.error('Sign-up error:', error);
    showError(getErrorMessage(error.code));
    isSigningIn = false;
  }
}

// Sign in with Google
export async function signInWithGoogle() {
  if (isSigningIn) return;

  isSigningIn = true;
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

    if (isNewUser) {
      await createUserData(result.user);
    } else {
      showSuccess('Successfully signed in with Google!');
    }
    isSigningIn = false;
  } catch (error) {
    console.error('Google sign-in error:', error);
    showError(getErrorMessage(error.code));
    isSigningIn = false;
  }
}

// Sign out
export async function signOut() {
  try {
    await firebaseSignOut(auth);
    showSuccess('Successfully signed out!');
  } catch (error) {
    console.error('Sign-out error:', error);
    showError('Error signing out. Please try again.');
  }
}

// Hide initial loading screen with minimum display time
async function hideAppLoading() {
  if (!appLoadingContainer) return;
  
  const minLoadTime = 800; // Minimum 800ms to prevent flash
  const elapsed = Date.now() - loadStartTime;
  
  if (elapsed < minLoadTime) {
    await new Promise(resolve => setTimeout(resolve, minLoadTime - elapsed));
  }
  
  appLoadingContainer.style.display = 'none';
}

// Display authentication UI
export async function showAuth() {
  await hideAppLoading();
  if (authContainer) authContainer.classList.remove('hidden');
  if (mainContainer) mainContainer.classList.add('hidden');
  if (authLoading) authLoading.style.display = 'none';

  // Handle ?mode=verifyEmail&oobCode=xxx — link landed directly on the app
  await handleEmailVerificationCode();

  // Handle ?signup=true URL parameter — open sign-up form directly
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('signup') === 'true') {
    window.history.replaceState({}, document.title, window.location.pathname);
    const signinFormEl = document.getElementById('signin-form');
    const signupFormEl = document.getElementById('signup-form');
    const toggleTextEl = document.getElementById('auth-toggle-text');
    const container = document.getElementById('auth-container');
    if (signinFormEl) signinFormEl.classList.add('hidden');
    if (signupFormEl) signupFormEl.classList.remove('hidden');
    if (container) container.classList.add('auth-container--signup');
    if (toggleTextEl) {
      toggleTextEl.innerHTML = 'Already have an account? <a id="auth-toggle-link">Sign in</a>';
    }
  }
}

// Display email verification pending screen
export async function showVerificationPending(user) {
  await hideAppLoading();
  if (authContainer) authContainer.classList.remove('hidden');
  if (mainContainer) mainContainer.classList.add('hidden');
  if (authLoading) authLoading.style.display = 'none';

  // If the user landed here via a verification link (?mode=verifyEmail&oobCode=...),
  // process the code immediately. showAuth() won't be reached in this path because
  // onAuthStateChanged sees an unverified signed-in user and routes here instead.
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('mode') === 'verifyEmail' && urlParams.get('oobCode')) {
    window.history.replaceState({}, document.title, window.location.pathname);
    try {
      await applyActionCode(auth, urlParams.get('oobCode'));
      await user.reload();
      if (auth.currentUser && auth.currentUser.emailVerified) {
        // Verified — transition straight to the app
        currentUser = auth.currentUser;
        hideVerificationPending();
        await showApp();
        initializeSubscription(currentUser);
        handleCheckoutSuccess();
        handleCheckoutCanceled();
        updateCurrentAccountDisplay();
        if (_onUserChangeCallback) await _onUserChangeCallback(currentUser);
        try {
          await setDoc(doc(db, 'users', currentUser.uid), {
            lastLoginAt: new Date().toISOString()
          }, { merge: true });
        } catch (e) {
          console.error('Error updating lastLoginAt:', e);
        }
        return;
      }
    } catch (error) {
      console.error('Error applying verification code from URL:', error);
      // Fall through to show the pending screen with an error
      showError('This verification link is invalid or has expired. Please request a new one.');
    }
  }

  // Hide sign-in/sign-up forms and footer toggle
  const signinFormEl = document.getElementById('signin-form');
  const signupFormEl = document.getElementById('signup-form');
  const resetFormEl = document.getElementById('password-reset-form-inline');
  const toggleEl = document.querySelector('.auth-toggle');

  if (signinFormEl) signinFormEl.classList.add('hidden');
  if (signupFormEl) signupFormEl.classList.add('hidden');
  if (resetFormEl) resetFormEl.classList.add('hidden');
  if (toggleEl) toggleEl.classList.add('hidden');

  // Populate email and show pending panel
  const emailDisplay = document.getElementById('verification-email-display');
  if (emailDisplay) emailDisplay.textContent = user.email;

  const pendingPanel = document.getElementById('verification-pending');
  if (pendingPanel) pendingPanel.classList.remove('hidden');

  updateResendButtonState();
}

// Hide verification pending screen and restore auth footer
function hideVerificationPending() {
  const pendingPanel = document.getElementById('verification-pending');
  if (pendingPanel) pendingPanel.classList.add('hidden');

  const toggleEl = document.querySelector('.auth-toggle');
  if (toggleEl) toggleEl.classList.remove('hidden');
}

// Manage 60-second cooldown countdown on the Resend button
function updateResendButtonState() {
  const resendBtn = document.getElementById('resend-verification-btn');
  if (!resendBtn) return;

  const elapsed = Date.now() - verificationResendTimestamp;
  const remaining = Math.ceil((RESEND_COOLDOWN_MS - elapsed) / 1000);

  if (verificationResendTimestamp > 0 && elapsed < RESEND_COOLDOWN_MS) {
    resendBtn.disabled = true;
    resendBtn.textContent = `Resend Email (${remaining}s)`;
    setTimeout(updateResendButtonState, 1000);
  } else {
    resendBtn.disabled = false;
    resendBtn.textContent = 'Resend Verification Email';
  }
}

// Send email verification and update resend cooldown.
// actionCodeSettings.url is the "continue URL" — after Firebase verifies the email
// on their page, users see a "Continue" button that brings them back to the app.
// If you've also set a custom Action URL in Firebase Console (Auth → Templates →
// Email Verification → Customize action URL → your domain), the link skips
// Firebase's page entirely and lands on the app, where handleEmailVerificationCode()
// below processes the oobCode automatically.
async function sendVerificationEmail(user) {
  const actionCodeSettings = {
    url: window.location.origin
  };
  try {
    await sendEmailVerification(user, actionCodeSettings);
    verificationResendTimestamp = Date.now();
    updateResendButtonState();
    return { success: true };
  } catch (error) {
    console.error('Error sending verification email:', error);
    return { success: false, error: getErrorMessage(error.code) };
  }
}

// Handle Firebase auth action URLs (?mode=...&oobCode=...) that land on the app
// when a custom action URL is configured in Firebase Console.
// Called from showAuth() on page load. Returns true if a mode was found and handled.
export async function handleEmailVerificationCode() {
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode');
  const oobCode = urlParams.get('oobCode');

  if (!mode || !oobCode) return false;

  // Clean the URL immediately so a refresh doesn't re-attempt the same code
  window.history.replaceState({}, document.title, window.location.pathname);

  switch (mode) {
    case 'verifyEmail':
      await handleVerifyEmail(oobCode);
      return true;
    case 'resetPassword':
      await handleResetPassword(oobCode);
      return true;
    case 'recoverEmail':
      await handleRecoverEmail(oobCode);
      return true;
    default:
      return false;
  }
}

// Process email verification oobCode
async function handleVerifyEmail(oobCode) {
  try {
    await applyActionCode(auth, oobCode);
    if (auth.currentUser) await auth.currentUser.reload();
    showSuccess('Email verified! You can now sign in.');
  } catch (error) {
    console.error('Error applying verification code:', error);
    showError('This verification link is invalid or has expired. Please request a new one.');
  }
}

// Process password reset oobCode — verify the code then show a new-password form
async function handleResetPassword(oobCode) {
  try {
    // Verify the code is valid and get the associated email
    const email = await verifyPasswordResetCode(auth, oobCode);
    showPasswordResetConfirmForm(email, oobCode);
  } catch (error) {
    console.error('Error verifying password reset code:', error);
    showError('This password reset link is invalid or has expired. Please request a new one.');
  }
}

// Render an inline form for the user to enter their new password
function showPasswordResetConfirmForm(email, oobCode) {
  // Hide all other auth forms
  ['signin-form', 'signup-form', 'password-reset-form-inline'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.classList.add('hidden');
  });
  const toggleEl = document.querySelector('.auth-toggle');
  if (toggleEl) toggleEl.classList.add('hidden');

  let confirmForm = document.getElementById('password-reset-confirm-form');
  if (!confirmForm) {
    confirmForm = document.createElement('form');
    confirmForm.id = 'password-reset-confirm-form';
    confirmForm.className = 'auth-form';
    confirmForm.innerHTML = `
      <h2 style="text-align:center;margin-bottom:24px;color:var(--text-dark);">
        Set New Password
      </h2>
      <p style="text-align:center;margin-bottom:24px;color:var(--text-light);font-size:0.9em;">
        Setting a new password for <strong id="reset-confirm-email"></strong>
      </p>
      <div class="form-group">
        <label for="reset-new-password">New Password</label>
        <div class="password-input-wrapper">
          <input type="password" id="reset-new-password" required minlength="6"
                 placeholder="At least 6 characters">
          <button type="button" class="password-toggle" data-target="reset-new-password" title="Show Password">
            <span class="password-toggle-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg></span>
          </button>
        </div>
      </div>
      <div class="form-group">
        <label for="reset-confirm-password">Confirm Password</label>
        <div class="password-input-wrapper">
          <input type="password" id="reset-confirm-password" required minlength="6"
                 placeholder="Repeat new password">
          <button type="button" class="password-toggle" data-target="reset-confirm-password" title="Show Password">
            <span class="password-toggle-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg></span>
          </button>
        </div>
      </div>
      <div class="error" id="reset-confirm-error" style="display:none;"></div>
      <div class="success" id="reset-confirm-success" style="display:none;"></div>
      <button type="submit" class="btn primary full-width" style="margin-bottom:16px;">
        Update Password
      </button>
    `;

    // Insert after sign-up form (or fall back to auth container)
    const signupFormEl = document.getElementById('signup-form');
    if (signupFormEl && signupFormEl.parentNode) {
      signupFormEl.parentNode.insertBefore(confirmForm, signupFormEl.nextSibling);
    } else {
      const container = document.querySelector('.auth-container') || document.getElementById('auth-container');
      if (container) container.appendChild(confirmForm);
    }
  }

  const emailDisplay = confirmForm.querySelector('#reset-confirm-email');
  if (emailDisplay) emailDisplay.textContent = email;

  // Store the oobCode on the form so the submit handler can read it
  confirmForm.dataset.oobCode = oobCode;
  confirmForm.classList.remove('hidden');

  // Wire up submit (replace listener each time to avoid duplicates)
  confirmForm.replaceWith(confirmForm.cloneNode(true));
  confirmForm = document.getElementById('password-reset-confirm-form');
  confirmForm.dataset.oobCode = oobCode;
  confirmForm.addEventListener('submit', handlePasswordResetConfirmSubmit);
}

async function handlePasswordResetConfirmSubmit(e) {
  e.preventDefault();
  const form = e.currentTarget;
  const oobCode = form.dataset.oobCode;
  const newPassword = document.getElementById('reset-new-password')?.value;
  const confirmPassword = document.getElementById('reset-confirm-password')?.value;
  const errorEl = document.getElementById('reset-confirm-error');
  const successEl = document.getElementById('reset-confirm-success');

  if (errorEl) errorEl.style.display = 'none';
  if (successEl) successEl.style.display = 'none';

  if (newPassword !== confirmPassword) {
    if (errorEl) { errorEl.textContent = 'Passwords do not match.'; errorEl.style.display = 'block'; }
    return;
  }

  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
    if (successEl) {
      successEl.textContent = 'Password updated! You can now sign in with your new password.';
      successEl.style.display = 'block';
    }
    // After a short delay, hide this form and show the sign-in form
    setTimeout(() => {
      form.classList.add('hidden');
      const toggleEl = document.querySelector('.auth-toggle');
      if (toggleEl) toggleEl.classList.remove('hidden');
      const signinFormEl = document.getElementById('signin-form');
      if (signinFormEl) signinFormEl.classList.remove('hidden');
    }, 2500);
  } catch (error) {
    console.error('Error confirming password reset:', error);
    const msg = error.code === 'auth/weak-password'
      ? 'Password should be at least 6 characters long.'
      : 'Failed to update password. The link may have expired — please request a new one.';
    if (errorEl) { errorEl.textContent = msg; errorEl.style.display = 'block'; }
  }
}

// Process email recovery oobCode (restores original email after an email-change)
async function handleRecoverEmail(oobCode) {
  try {
    const info = await checkActionCode(auth, oobCode);
    const restoredEmail = info.data.email;
    await applyActionCode(auth, oobCode);
    if (auth.currentUser) await auth.currentUser.reload();
    showSuccess(`Email restored to ${restoredEmail}. You can now sign in.`);
  } catch (error) {
    console.error('Error recovering email:', error);
    showError('This email recovery link is invalid or has expired. Please contact support.');
  }
}

// Check if the user has verified their email and transition to the app
async function checkEmailVerification() {
  const checkBtn = document.getElementById('check-verification-btn');
  const user = auth.currentUser;
  if (!user) return;

  if (checkBtn) {
    checkBtn.disabled = true;
    checkBtn.textContent = 'Checking...';
  }

  try {
    await user.reload();
    const refreshedUser = auth.currentUser;

    if (refreshedUser && refreshedUser.emailVerified) {
      currentUser = refreshedUser;
      hideVerificationPending();
      await showApp();
      initializeSubscription(refreshedUser);
      handleCheckoutSuccess();
      handleCheckoutCanceled();
      updateCurrentAccountDisplay();
      if (_onUserChangeCallback) await _onUserChangeCallback(refreshedUser);
      try {
        await setDoc(doc(db, 'users', refreshedUser.uid), {
          lastLoginAt: new Date().toISOString()
        }, { merge: true });
      } catch (e) {
        console.error('Error updating lastLoginAt:', e);
      }
    } else {
      showError('Email not yet verified. Please check your inbox and click the link.');
      if (checkBtn) {
        checkBtn.disabled = false;
        checkBtn.textContent = 'Check / I\'ve Verified';
      }
    }
  } catch (error) {
    console.error('Error reloading user:', error);
    showError('Could not check verification status. Please try again.');
    if (checkBtn) {
      checkBtn.disabled = false;
      checkBtn.textContent = 'Check / I\'ve Verified';
    }
  }
}

// Display main application
export async function showApp() {
  await hideAppLoading();
  if (authContainer) authContainer.classList.add('hidden');
  if (mainContainer) mainContainer.classList.remove('hidden');
}

// Toggle between sign-in and sign-up forms
export function toggleAuthForm() {
  // Get form elements fresh
  const currentSigninForm = document.getElementById('signin-form');
  const currentSignupForm = document.getElementById('signup-form');
  const currentAuthToggleText = document.getElementById('auth-toggle-text');
  const resetForm = document.getElementById('password-reset-form-inline');
  const container = document.getElementById('auth-container');

  if (!currentSigninForm || !currentSignupForm || !currentAuthToggleText) return;

  // Hide password reset form if it's visible
  if (resetForm && !resetForm.classList.contains('hidden')) {
    resetForm.classList.add('hidden');
  }

  const isSignupVisible = !currentSignupForm.classList.contains('hidden');

  if (isSignupVisible) {
    // Switch to sign-in
    currentSignupForm.classList.add('hidden');
    currentSigninForm.classList.remove('hidden');
    currentAuthToggleText.innerHTML = 'Don\'t have an account? <a id="auth-toggle-link">Start for free</a>';
    if (container) container.classList.remove('auth-container--signup');
  } else {
    // Switch to sign-up
    currentSigninForm.classList.add('hidden');
    currentSignupForm.classList.remove('hidden');
    currentAuthToggleText.innerHTML = 'Already have an account? <a id="auth-toggle-link">Sign in</a>';
    if (container) container.classList.add('auth-container--signup');
  }
}

// Show error message
export function showError(message) {
  if (!authError) return;
  
  authError.textContent = message;
  authError.style.display = 'block';
  if (authSuccess) authSuccess.style.display = 'none';
  
  // Auto-hide after 5 seconds
  setTimeout(() => {
    if (authError) authError.style.display = 'none';
  }, 5000);
}

// Show success message
export function showSuccess(message) {
  if (!authSuccess) return;
  
  authSuccess.textContent = message;
  authSuccess.style.display = 'block';
  if (authError) authError.style.display = 'none';
  
  // Auto-hide after 3 seconds
  setTimeout(() => {
    if (authSuccess) authSuccess.style.display = 'none';
  }, 3000);
}

// Convert Firebase error codes to user-friendly messages
export function getErrorMessage(errorCode) {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/missing-email':
      return 'Please enter your email address.';
    default:
      return 'An error occurred. Please try again.';
  }
}

// Get current user
export function getCurrentUser() {
  return currentUser;
}

// Create initial user data with membership info
// New users get a 30-day free trial, no payment required upfront
async function createUserData(user) {
  const now = new Date();
  const trialExpiry = new Date(now);
  trialExpiry.setDate(trialExpiry.getDate() + 30);

  const userData = {
    email: user.email,
    membership: {
      status: 'trial',
      signupDate: now.toISOString(),
      trialExpiry: trialExpiry
    }
  };

  try {
    await setDoc(doc(db, 'users', user.uid), userData, { merge: true });
    console.log('User membership data created successfully');
  } catch (error) {
    console.error('Error creating user data:', error);
  }
}

// Set up authentication state observer
export function initializeAuth(onUserChange) {
  _onUserChangeCallback = onUserChange;

  return onAuthStateChanged(auth, async (user) => {
    // Clear budget data when switching users or signing out
    if (currentUser && (!user || currentUser.uid !== user.uid)) {
      console.log('User changed, clearing previous user data');
      // Import and call clearBudgetData when switching users
      try {
        const { clearBudgetData } = await import('./budget.js');
        await clearBudgetData();
      } catch (error) {
        console.error('Error clearing budget data:', error);
      }

      // Cleanup subscription listener
      cleanupSubscription();
    }

    currentUser = user;
    authInitialized = true;

    if (user) {
      // Gate: require email verification for email/password accounts.
      // Google OAuth users have emailVerified === true automatically.
      if (!user.emailVerified) {
        console.log('User signed in but email not verified:', user.email);
        await showVerificationPending(user);
        return;
      }

      console.log('User signed in:', user.email);
      await showApp();

      // Initialize subscription management and trial expiry checking
      initializeSubscription(user);

      // Handle Stripe checkout return URLs
      handleCheckoutSuccess();
      handleCheckoutCanceled();

      // Update current account display
      updateCurrentAccountDisplay();

      // Call the callback if provided (loads user data including linked account detection)
      if (onUserChange && typeof onUserChange === 'function') {
        await onUserChange(user);
      }

      // Track last login AFTER data load to avoid partial-document cache pollution.
      // setDoc(merge:true) on an uncached document writes only the specified fields to
      // the local Firestore cache, which would cause loadUserData to miss linkedAccount.
      try {
        await setDoc(doc(db, 'users', user.uid), {
          lastLoginAt: new Date().toISOString()
        }, { merge: true });
      } catch (e) {
        console.error('Error updating lastLoginAt:', e);
      }
    } else {
      console.log('User signed out');
      currentUser = null;

      // Cleanup subscription listener
      cleanupSubscription();

      hideVerificationPending();
      await showAuth();
    }
  });
}

// Update current account display
function updateCurrentAccountDisplay() {
  const currentAccountEl = document.getElementById('current-account');
  if (currentAccountEl && currentUser) {
    currentAccountEl.textContent = currentUser.email;
  }
  
  // Update membership display
  updateMembershipDisplay();
}

// Get user membership data
export async function getUserMembershipData() {
  if (!currentUser) return null;
  
  try {
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.membership || null;
    }
  } catch (error) {
    console.error('Error fetching membership data:', error);
  }
  return null;
}

// Update membership display in UI
export async function updateMembershipDisplay() {
  // Use the subscription module's active listener data so linked users
  // inherit the owner's membership status rather than their own.
  const { getMembershipData } = await import('./subscription.js');
  const membershipData = getMembershipData();

  if (!membershipData || !membershipData.status) return;
  
  // Update main membership badge
  const membershipBadge = document.getElementById('membership-badge');
  const membershipBadgeSmall = document.getElementById('membership-badge-small');
  const membershipDetails = document.getElementById('membership-details');
  const memberSince = document.getElementById('member-since');
  
  const status = membershipData.status || 'trial';
  const trialExpiry = membershipData.trialExpiry
    ? (typeof membershipData.trialExpiry.toDate === 'function' ? membershipData.trialExpiry.toDate() : new Date(membershipData.trialExpiry))
    : null;
  
  // Update badge text and style
  const badgeText = status === 'trial' ? 'Free Trial' : 
                   status === 'premium' ? 'Premium' : 
                   status === 'expired' ? 'Expired' : 'Free Trial';
  
  if (membershipBadge) {
    membershipBadge.textContent = badgeText;
    membershipBadge.className = `membership-badge ${status}`;
  }
  
  if (membershipBadgeSmall) {
    membershipBadgeSmall.textContent = badgeText;
    membershipBadgeSmall.className = `membership-badge-small ${status}`;
  }
  
  // Update details
  if (membershipDetails) {
    if (status === 'trial' && trialExpiry) {
      const daysLeft = Math.ceil((trialExpiry - new Date()) / (1000 * 60 * 60 * 24));
      membershipDetails.textContent = daysLeft > 0
        ? `Trial expires in ${daysLeft} days (${trialExpiry.toLocaleDateString()})`
        : 'Trial expired';
    } else if (status === 'premium') {
      membershipDetails.textContent = 'Active subscription';
    } else {
      membershipDetails.textContent = 'Trial expired';
    }
  }
}

// Initialize authentication event listeners
export function setupAuthEventListeners() {
  // Sign-in form
  if (signinForm) {
    signinForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = document.getElementById('signin-email')?.value;
      const password = document.getElementById('signin-password')?.value;
      if (email && password) {
        await signIn(email, password);
      }
    });
  }
  
  // Sign-up form
  if (signupForm) {
    signupForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = document.getElementById('signup-email')?.value;
      const password = document.getElementById('signup-password')?.value;
      const confirmPassword = document.getElementById('signup-confirm')?.value;
      
      if (password !== confirmPassword) {
        showError('Passwords do not match.');
        return;
      }
      
      if (email && password) {
        await signUp(email, password);
      }
    });
  }
  
  // Google sign-in buttons
  const googleSigninBtn = document.getElementById('google-signin-btn');
  const googleSignupBtn = document.getElementById('google-signup-btn');
  
  if (googleSigninBtn) {
    googleSigninBtn.addEventListener('click', signInWithGoogle);
  }
  
  if (googleSignupBtn) {
    googleSignupBtn.addEventListener('click', signInWithGoogle);
  }
  
  // Auth form toggle - use event delegation to handle dynamic content
  const authToggleText = document.getElementById('auth-toggle-text');
  if (authToggleText) {
    authToggleText.addEventListener('click', (e) => {
      if (e.target.id === 'auth-toggle-link') {
        e.preventDefault();
        toggleAuthForm();
      }
    });
  }
  
  // Forgot password link - use event delegation to handle potential dynamic updates
  document.addEventListener('click', (e) => {
    if (e.target.id === 'forgot-password-link') {
      e.preventDefault();
      showPasswordResetModal();
    }
  });

  // Verification pending: check if email has been verified
  document.addEventListener('click', async (e) => {
    if (e.target.id === 'check-verification-btn') {
      await checkEmailVerification();
    }
  });

  // Verification pending: resend verification email
  document.addEventListener('click', async (e) => {
    if (e.target.id === 'resend-verification-btn' && !e.target.disabled) {
      const user = auth.currentUser;
      if (!user) return;
      const result = await sendVerificationEmail(user);
      if (result.success) {
        showSuccess('Verification email sent! Please check your inbox.');
      } else {
        showError(result.error || 'Failed to send verification email.');
      }
    }
  });

  // Verification pending: sign out and use a different account
  document.addEventListener('click', async (e) => {
    if (e.target.id === 'verification-signout-link') {
      e.preventDefault();
      await signOut();
    }
  });

  // Password toggle functionality
  setupPasswordToggle();
}

// Setup password visibility toggle functionality
function setupPasswordToggle() {
  // Use event delegation to handle all password toggle buttons
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('password-toggle') || e.target.closest('.password-toggle')) {
      const toggleButton = e.target.classList.contains('password-toggle') ? e.target : e.target.closest('.password-toggle');
      const targetId = toggleButton.getAttribute('data-target');
      const passwordInput = document.getElementById(targetId);
      
      if (passwordInput) {
        const isVisible = passwordInput.type === 'text';
        
        if (isVisible) {
          // Hide password
          passwordInput.type = 'password';
          toggleButton.setAttribute('data-visible', 'false');
          toggleButton.title = 'Show Password';
          const icon = toggleButton.querySelector('.password-toggle-icon');
          if (icon) icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>';
        } else {
          // Show password
          passwordInput.type = 'text';
          toggleButton.setAttribute('data-visible', 'true');
          toggleButton.title = 'Hide Password';
          const icon = toggleButton.querySelector('.password-toggle-icon');
          if (icon) icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>';
        }
      }
    }
  });
}

// Export auth state for global access
export { currentUser, isSigningIn };